import {
  addDays,
  deviceTimeZone,
  LOG_SASS_RATE,
  localDateString,
  type MealSlot,
  scaleToGrams,
} from '@gramas/shared'
import { Button, MacroPreview, NumberField, Sheet } from '@gramas/ui'
import { useLiveQuery } from 'dexie-react-hooks'
import { useEffect, useMemo, useState } from 'react'
import { db, type LocalLogEntry } from '../../db/dexie.js'
import {
  deleteEntry,
  moveEntry,
  repeatEntry,
  restoreEntry,
  updateEntryQuantity,
} from '../../db/repo.js'
import { sassLine } from '../../sass/engine.js'
import { sassToast } from '../../sass/toast.js'
import { showSassToast } from '../../shell/toastStore.js'
import { syncNow } from '../../sync/engine.js'
import { QuantityPresets } from './QuantityPresets.js'
import { SlotPicker } from './SlotPicker.js'

export interface EditEntrySheetProps {
  entry: LocalLogEntry | null
  onOpenChange: (open: boolean) => void
}

/**
 * Correct or remove a logged item.
 *
 * Scaling works off the entry's own snapshot rather than re-reading the food:
 * the whole point of the snapshot is that a later correction to the product
 * must not silently rewrite what was eaten, so a quantity change rescales the
 * numbers that were true at the time.
 */
export function EditEntrySheet({ entry, onOpenChange }: EditEntrySheetProps) {
  const [grams, setGrams] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)
  const today = localDateString(new Date(), deviceTimeZone())

  // The entry keeps a macro snapshot but not the unit, so the product it came
  // from is what says whether this was poured or weighed.
  const unit = useLiveQuery(
    async () => (entry?.foodId ? ((await db.foods.get(entry.foodId))?.unit ?? 'g') : 'g'),
    [entry?.foodId],
    'g' as const,
  )

  useEffect(() => {
    if (entry) setGrams(entry.quantityG)
  }, [entry])

  /**
   * A portion-priced entry is counted in portions, not grams.
   *
   * The chain published no weight, so the grams on the row are the nominal
   * denominator its figures are stored against — editing them is really editing
   * how many portions were eaten, and that is what the field should say.
   */
  const servings = entry?.servings ?? null
  const portionUnit = entry != null && servings != null && servings > 0
  const perPortionG = portionUnit && servings != null ? entry.quantityG / servings : 1
  const shown = portionUnit && grams != null ? grams / perPortionG : grams
  const setShown = (value: number | null) =>
    setGrams(value == null ? null : portionUnit ? value * perPortionG : value)

  const preview = useMemo(() => {
    if (!entry || !grams || entry.quantityG <= 0) return null
    const per100g = {
      kcal: (entry.kcal * 100) / entry.quantityG,
      protein: (entry.protein * 100) / entry.quantityG,
      carbs: (entry.carbs * 100) / entry.quantityG,
      fat: (entry.fat * 100) / entry.quantityG,
    }
    return scaleToGrams(per100g, grams)
  }, [entry, grams])

  const commit = async () => {
    if (!entry || !grams || busy) return
    setBusy(true)
    try {
      await updateEntryQuantity(entry.id, grams)
      void syncNow()
      onOpenChange(false)
      // An edit had no feedback at all; with the voice on it earns a remark
      // sometimes. The neutral mode keeps its silence.
      if (Math.random() < LOG_SASS_RATE) {
        const line = sassLine('entry_edited', { itemName: entry.snapshotName })
        if (line != null) showSassToast(line)
      }
    } finally {
      setBusy(false)
    }
  }

  /**
   * Put the row where it belonged. The sheet stays open and the entry it is
   * editing is the same one, so a mis-slotted lunch can be corrected and its
   * quantity fixed in the same visit.
   */
  const move = async (to: { slot?: MealSlot; logDate?: string }) => {
    if (!entry || busy) return
    setBusy(true)
    try {
      await moveEntry(entry.id, to)
      void syncNow()
      onOpenChange(false)
      // A silent move is genuinely confusing — the row vanishes from where the
      // eye was looking — so the voice always confirms where it went.
      if (to.slot != null) {
        const line = sassLine('entry_moved', { slot: to.slot, itemName: entry.snapshotName })
        if (line != null) showSassToast(line)
      }
    } finally {
      setBusy(false)
    }
  }

  const repeat = async () => {
    if (!entry || busy) return
    setBusy(true)
    try {
      await repeatEntry(entry.id)
      void syncNow()
      onOpenChange(false)
    } finally {
      setBusy(false)
    }
  }

  const remove = async () => {
    if (!entry || busy) return
    setBusy(true)
    try {
      const { id, snapshotName } = entry
      await deleteEntry(id)
      void syncNow()
      onOpenChange(false)
      sassToast('entry_deleted', { itemName: snapshotName }, `Pašalinta: ${snapshotName}`, {
        label: 'Atšaukti',
        run: async () => {
          await restoreEntry(id)
          void syncNow()
        },
      })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Sheet
      open={entry != null}
      onOpenChange={onOpenChange}
      title={entry?.snapshotName ?? ''}
      description={
        entry == null
          ? undefined
          : portionUnit
            ? `Įrašyta ${Math.round((servings ?? 0) * 10) / 10} porcijos`
            : `Įrašyta ${Math.round(entry.quantityG)} ${unit}`
      }
      footer={
        <div className="flex gap-2">
          <Button variant="danger" onClick={() => void remove()} disabled={busy}>
            Šalinti
          </Button>
          <Button
            variant="primary"
            block
            size="lg"
            onClick={() => void commit()}
            disabled={busy || !grams || grams === entry?.quantityG}
          >
            Įrašyti {preview ? `${Math.round(preview.kcal)} kcal` : ''}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4 px-4 py-4">
        <NumberField
          label="Kiekis"
          suffix={portionUnit ? 'porcijos' : unit}
          value={shown}
          onChange={setShown}
        />
        {!portionUnit && (
          <QuantityPresets unit={unit} value={grams} servingSizeG={null} onPick={setGrams} />
        )}

        {/* Correcting where a row sits used to mean deleting it and logging it
            again, which throws away the food it came from and the snapshot of
            what that food said at the time. */}
        {entry && (
          <>
            <SlotPicker value={entry.slot} onChange={(slot) => void move({ slot })} />
            <div className="flex items-center justify-between">
              <span className="text-sm text-dim">Diena</span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  disabled={busy}
                  onClick={() => void move({ logDate: addDays(entry.logDate, -1) })}
                >
                  ← Vakar
                </Button>
                <span className="tnum text-sm text-mute">{entry.logDate}</span>
                <Button
                  size="sm"
                  disabled={busy || entry.logDate >= today}
                  onClick={() => void move({ logDate: addDays(entry.logDate, 1) })}
                >
                  Rytoj →
                </Button>
              </div>
            </div>
          </>
        )}

        <button
          type="button"
          onClick={() => void repeat()}
          disabled={busy}
          className="self-start text-sm text-accent-text"
        >
          Kartoti šiandien
        </button>
        {preview && (
          <MacroPreview
            kcal={preview.kcal}
            proteinG={preview.protein}
            carbsG={preview.carbs}
            fatG={preview.fat}
          />
        )}
      </div>
    </Sheet>
  )
}
